import React from "react";
import CrudPage, { Badge } from "@/components/CrudPage";
import { formatDate, formatKM, deadlineLabel } from "@/lib/format";
import { EXPENSE_CATEGORIES, INVOICE_STATUS } from "@/lib/troskovi";
import { CheckCircle2 } from "lucide-react";

const statusTone = { "Plaćeno": "success", Neplaćeno: "warning", "Djelimično": "info" };

function outstanding(r) {
  if (r.status === "Plaćeno") return 0;
  return Math.max(0, Number(r.amount || 0) - Number(r.paid_amount || 0));
}

export default function Invoices() {
  const compute = (r) => ({ ...r, _dug: outstanding(r) });

  const columns = [
    { key: "supplier_name", label: "Dobavljač / Firma", render: (r) => <span className="font-medium">{r.supplier_name}</span> },
    { key: "invoice_number", label: "Broj računa", render: (r) => <span className="font-mono text-sm">{r.invoice_number || "—"}</span> },
    { key: "ziro_racun", label: "Žiro račun za uplatu", render: (r) => <span className="font-mono text-sm">{r.ziro_racun || "—"}</span>, export: (r) => r.ziro_racun || "" },
    { key: "is_fixed", label: "Fiksni", render: (r) => (r.is_fixed ? <Badge tone="info">Fiksni</Badge> : "—"), export: (r) => (r.is_fixed ? "Da" : "") },
    { key: "pdv_mode", label: "PDV", render: (r) => { const m = r.pdv_mode || (r.pdv_deductible ? "Odbitni PDV" : "PDV (nije odbitni)"); return <Badge tone={m === "Odbitni PDV" ? "success" : m === "Bez PDV-a" ? "info" : "muted"}>{m}</Badge>; }, export: (r) => r.pdv_mode || (r.pdv_deductible ? "Odbitni PDV" : "PDV (nije odbitni)") },
    { key: "reinvoiced", label: "Prefakturisano", render: (r) => (r.reinvoiced ? <Badge tone="warning">Kupcu</Badge> : "—"), export: (r) => (r.reinvoiced ? "Da" : "") },
    { key: "category", label: "Kategorija troška", render: (r) => <Badge tone="muted">{r.category || "—"}</Badge> },
    { key: "amount", label: "Iznos (sa PDV)", render: (r) => <span className="tabular-nums font-semibold">{formatKM(r.amount)}</span>, export: (r) => r.amount },
    { key: "_dug", label: "Dug (ostalo)", render: (r) => <span className="tabular-nums">{formatKM(r._dug)}</span>, export: (r) => r._dug },
    { key: "due_date", label: "Rok plaćanja", render: (r) => formatDate(r.due_date), export: (r) => r.due_date },
    {
      key: "_rok", label: "Do roka",
      render: (r) => {
        if (r.status === "Plaćeno") return <Badge tone="success">Plaćeno</Badge>;
        const l = deadlineLabel(r.due_date);
        return <Badge tone={l.tone}>{l.text}</Badge>;
      },
      export: (r) => (r.status === "Plaćeno" ? "Plaćeno" : deadlineLabel(r.due_date).text),
    },
    { key: "status", label: "Status", render: (r) => <Badge tone={statusTone[r.status] || "muted"}>{r.status}</Badge> },
  ];

  const fields = [
    {
      key: "_picker", label: "Odaberi spremljenog dobavljača (imenik)", type: "directory", full: true,
      directory: {
        endpoint: "suppliers",
        placeholder: "Odaberi dobavljača iz imenika...",
        getLabel: (s) => `${s.supplier_name}${s.ziro_racun ? " · " + s.ziro_racun : ""}`,
        createFields: [
          { key: "supplier_name", label: "Dobavljač / Firma", required: true },
          { key: "ziro_racun", label: "Broj žiro računa za uplatu" },
        ],
        map: (s) => ({ supplier_name: s.supplier_name, ziro_racun: s.ziro_racun }),
      },
    },
    { key: "supplier_name", label: "Dobavljač / Firma", required: true },
    { key: "invoice_number", label: "Broj računa" },
    { key: "ziro_racun", label: "Broj žiro računa za uplatu" },
    { key: "category", label: "Kategorija troška", type: "category", categoryType: "fakture" },
    { key: "issue_date", label: "Datum fakture", type: "date" },
    { key: "due_date", label: "Rok plaćanja", type: "date", required: true },
    { key: "amount", label: "Iznos sa PDV-om (KM)", type: "number", step: "0.01", required: true },
    { key: "paid_amount", label: "Plaćeni iznos (KM)", type: "number", step: "0.01" },
    { key: "status", label: "Status", type: "select", options: INVOICE_STATUS },
    { key: "is_fixed", label: "Fiksni trošak", type: "checkbox", checkboxLabel: "Ovo je fiksni / ponavljajući trošak (interna evidencija)" },
    { key: "pdv_mode", label: "PDV status", type: "select", options: ["Odbitni PDV", "PDV (nije odbitni)", "Bez PDV-a"] },
    { key: "reinvoiced", label: "Prefakturisano kupcu", type: "checkbox", checkboxLabel: "Naplaćeno/fakturisano kupcu — NE ide u trošak (samo evidencija)" },
    { key: "note", label: "Napomena", type: "textarea", full: true },
  ];

  const rowActions = [
    {
      key: "paid",
      label: "Plaćeno",
      icon: CheckCircle2,
      tone: "success",
      successText: "Faktura označena plaćenom",
      visible: (r) => r.status !== "Plaćeno",
      patch: (r) => ({ status: "Plaćeno", paid_amount: Number(r.amount) || 0 }),
    },
  ];

  return (
    <CrudPage
      title="Fakture dobavljača / Dug"
      subtitle="Unesite svaku ulaznu fakturu (sa brojem računa i žiro računom). Označite kvačicom 'Fiksni trošak' za ponavljajuće troškove. Filtrirajte 'Neplaćeno' da vidite dug."
      endpoint="invoices"
      columns={columns}
      fields={fields}
      testid="invoices"
      computeRow={compute}
      emptyDefaults={{ status: "Neplaćeno", issue_date: new Date().toISOString().slice(0, 10), paid_amount: 0, is_fixed: false, pdv_mode: "PDV (nije odbitni)", reinvoiced: false }}
      filterOptions={{ key: "status", label: "Status", options: INVOICE_STATUS }}
      rowActions={rowActions}
    />
  );
}
